import { useEffect, useRef, useState } from 'react';
import { readLegacyBinding, recordIntentionMadeToday, requireLegacy } from '../../legacy/legacyGlobals';
import { unmountExtractedScreen } from '../../legacy/screenHost';

const asHtml = (html: string) => ({ __html: html });

const CLOCK_IC = '<svg width="17" height="17" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="1.9" stroke-linecap="round" stroke-linejoin="round"><circle cx="12" cy="12" r="8.6"/><path d="M12 7.4V12l3 1.8"/></svg>';

interface IntentionScreenProps {
  /** The guided tour chains its next step through here (legacy opts.onDone). */
  onDone?: () => void;
}

/**
 * SCREEN 3 — set intention (legacy screenIntention): the duʿāʾ gathering
 * artwork, the cause card, and the dedication confirmation overlay. The
 * artwork and heart icon are read live from the legacy runtime's own
 * constants (still loaded), so they stay byte-identical by construction.
 * Making the intention starts the page prefetch immediately, exactly like
 * the original, so the answer is usually ready by "Begin reading".
 */
export function IntentionScreen({ onDone }: IntentionScreenProps) {
  const t = requireLegacy('t');
  const [confirming, setConfirming] = useState(false);
  /* A niyyah is made, not clicked: for three seconds the button shows a clock
     and asks for the intention, then unlocks with its real label. */
  const [armed, setArmed] = useState(false);

  useEffect(() => {
    const timer = window.setTimeout(() => setArmed(true), 3000);
    return () => window.clearTimeout(timer);
  }, []);

  const dedicate = () => {
    void requireLegacy('prefetchNextPage')();
    setConfirming(true);
  };

  return (
    <div className="iscreen">
      {/* an escape hatch for a mistaken tap — never during the guided tour,
          where the coach owns the journey */}
      {!onDone && (
        <button className="ibackbtn" onClick={() => requireLegacy('screenLanding')()}>
          {t('back')}
        </button>
      )}
      <div className="ibody">
        <div className="istage" dangerouslySetInnerHTML={asHtml(readLegacyBinding<string>('DUA_SCENE'))} />
        {/* translation strings render via innerHTML like the legacy el() calls —
            some copy (e.g. icBody) carries real markup such as <b> */}
        <div className="ikicker" dangerouslySetInnerHTML={asHtml('✦ ' + t('ikicker'))} />
        <h2 className="ititle" dangerouslySetInnerHTML={asHtml(t('ititle2'))} />
        <div
          className="icause"
          dangerouslySetInnerHTML={asHtml(
            '<div class="icheader">✦ ' + t('icheader') + '</div>'
            + '<img class="causeart" src="img/gaza-hero.jpg" alt="' + t('causeName') + '">'
            + '<div class="icbody">'
              + '<span class="ictag">' + t('causeTag') + ' · ' + t('causeWin') + '</span>'
              + '<b>' + t('causeHead') + '</b>'
              + '<p class="icblurb">' + t('causeBlurb') + '</p>'
            + '</div>',
          )}
        />
      </div>
      <div className="ifoot">
        <button
          className={'ibtn' + (armed ? '' : ' arming')}
          disabled={!armed}
          onClick={dedicate}
          dangerouslySetInnerHTML={asHtml(armed
            ? readLegacyBinding<string>('HEART_IC') + ' ' + t('ibtn')
            : CLOCK_IC + ' ' + t('intentArming'))}
        />
      </div>
      {confirming && <IntentionConfirmation onDone={onDone} />}
    </div>
  );
}

function IntentionConfirmation({ onDone }: IntentionScreenProps) {
  const t = requireLegacy('t');
  const [shown, setShown] = useState(false);
  const beginRef = useRef<HTMLButtonElement>(null);

  /* The original adds .show on the next animation frame so the overlay's
     entrance transition actually plays. */
  useEffect(() => {
    const frame = requestAnimationFrame(() => setShown(true));
    return () => cancelAnimationFrame(frame);
  }, []);

  const beginReading = () => {
    recordIntentionMadeToday();
    /* goToNextPage owns the button's disabled state and searching veil; the
       destination is a legacy screen, so unmount before it paints. */
    void requireLegacy('goToNextPage')(beginRef.current, () => {
      unmountExtractedScreen();
      if (onDone) onDone();
      else void requireLegacy('routeAfterIntention')();
    });
  };

  return (
    <div className={'iconfirm' + (shown ? ' show' : '')}>
      <span
        className="icbadge"
        dangerouslySetInnerHTML={asHtml('<svg width="40" height="40" viewBox="0 0 24 24" fill="none" stroke="#fff" stroke-width="2.4" stroke-linecap="round" stroke-linejoin="round"><path d="M5 13l4 4L19 7"/></svg>')}
      />
      <div className="icdua">تَقَبَّلَ ٱللَّهُ مِنك</div>
      <h3 dangerouslySetInnerHTML={asHtml(t('iconfirmH'))} />
      <p dangerouslySetInnerHTML={asHtml(t('icBody', { c: t('causeName') }))} />
      <button ref={beginRef} className="icbegin" onClick={beginReading} dangerouslySetInnerHTML={asHtml(t('ibegin'))} />
    </div>
  );
}
